<template>
  <div role="navigation" v-show="messagesAvailable">
    <b-navbar toggleable="lg" fixed="top" :sticky="true" class="header">
      <b-navbar-brand class="sr-only sr-only-focusable mr-2" href="#main-content">{{ $t('skip.to.main.content.label')}}</b-navbar-brand>
      <b-navbar-brand :href="homeurl" :title="$t('app.description')">
        <img src="../assets/icons/logo/home.png" height="70px" />
      </b-navbar-brand>

      <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav>
          <b-nav-item to="/user/" v-if="showUser" active-class="text-link">{{ $t('comp.header.user.label') }}</b-nav-item>
          <b-nav-item to="/encore/" v-if="showAdmin" active-class="text-link">{{ $t('comp.header.encore.label') }}</b-nav-item>
          <template v-for="item in headerLinks">
            <b-nav-item :target="item.target ||'_self'" :to="item.to" v-if="item.local" active-class="text-link" :key="item.id">{{ $t('comp.header.'+item.id+'.label') }}</b-nav-item>
            <b-nav-item :target="item.target ||'_self'" :href="item.url" v-else active-class="text-link" :key="item.id">{{ $t('comp.header.'+item.id+'.label') }}</b-nav-item>
          </template>
        </b-navbar-nav>
        <b-navbar-nav class="ml-auto">

          <b-nav-item-dropdown right>
            <template v-slot:button-content>
              <img src="../assets/icons/logo/language.svg" alt="Language switcher" class="svg-lang-switcher"/>
              {{ langName($root.$i18n.locale) }}
            </template>
            <b-dropdown-item v-for="lang in langs" :key="lang" @click="changeLocale(lang)" :lang="lang" class="text-center">
              {{ langFlag(lang) }} {{ langName(lang) }}
            </b-dropdown-item>
          </b-nav-item-dropdown>

          <TheSessionTimer/>

          <b-nav-item-dropdown v-if="currentUser" right>
            <template v-slot:button-content>
              <b-icon icon="person-fill" variant="dark"/>
            </template>
            <div style="min-width: 250px;">
              <div>
                {{ $t('springSecurity.login.username.label') }}: <em>{{ currentUser.username }}</em>
              </div>
                <div v-if="switched">
                  <b-button variant="warning" class="w-100 my-3" @click="resume"><b-icon-box-arrow-up-right/> {{ $t('comp.header.resume.as.label', { user: switchedUserOriginalUsername }) }}</b-button>
                </div>
                <div v-if="canSwitch" >
                  <b-button class="w-100 my-3" @click="showSwitchModal = true"><b-icon-box-arrow-in-down-left/> {{ $t('comp.header.switch.label') }}</b-button>
                </div>
<!--              </template>-->
              <b-button @click="logout" variant="secondary" class="w-100 mr-0" >
                <b-icon-box-arrow-right/> {{ $t('logout.button') }}
              </b-button>
            </div>
          </b-nav-item-dropdown>
          <b-nav-item v-else @click="showLoginModal = true"  active-class="text-link"><b-icon-box-arrow-in-right/> {{ $t("signin.label") }}</b-nav-item>
          <TheNodeWitcher v-if="showNodes"/>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>
    <b-modal
      v-model="showLoginModal"
      size="md"
      hide-footer
    >
      <div class="d-block">
        <b-container fluid>
          <b-row class="m-1">
            <b-col cols="12" :lg="12">
              <h2 class="text-center mb-3">{{ $t("signin.local.label") }}</h2>
              <b-form @submit="localLogin">
                <b-form-input type="text"     id="username" v-model="username" required :placeholder="$t('springSecurity.login.username.label')" class="mb-1"></b-form-input>
                <b-form-input type="password" id="password" v-model="password" required :placeholder="$t('springSecurity.login.password.label')" class="mb-1"></b-form-input>
                <b-form-input type="text"     id="token"    v-model="token"    required :placeholder="$t('springSecurity.login.token.label')" class="mb-1"></b-form-input>
                <b-button type="submit" variant="success" class="w-100 mb-3">{{ $t("springSecurity.login.title") }}</b-button>
                <div class="text-center"><b-link href="#" @click="gotoRecovery">{{ $t("forgotten.password.label") }}</b-link></div>
              </b-form>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-modal>
    <b-modal
        v-model="showSwitchModal"
        :title="$t('user.switch.title')"
        :ok-title="$t('user.switch.label', {user: switchId?switchId:'...'})"
        :ok-disabled="!switchId"
        :cancel-title="$t('cancel.label')"
        @ok="switchUser"
        @cancel="resetSwitchUser"
        size="lg"
    >
      <SelectUserMin :username.sync="switchIdMUser" show-username show-mail/>
    </b-modal>
  </div>
</template>

<script>
import AuthService from '@/services/auth.service'
import { asyncLanguageLoad } from '@/i18n'
import { authMixin } from '@/mixins/auth.mixin'
import { notificationsMixin, i18nMixin, routeAwareMixin } from '@rrze/vue-bixins'
import SelectUserMin from '@/components/root/SelectUserMin'
import TheNodeWitcher from '@/components/root/TheNodeWitcher'
import TheSessionTimer from '@/components/common/TheSessionTimer'

const ADMIN_ROLE = process.env.VUE_APP_ADMIN_ROLE

export default {
  name: 'TheHeader',
  i18nCompName: 'header',
  i18n: {
    messages: {}
  },
  props: {
    headerLinks: {
      default: () => [],
      type: Array
    }
  },
  components: { SelectUserMin, TheNodeWitcher, TheSessionTimer },
  mixins: [authMixin, notificationsMixin, i18nMixin, routeAwareMixin],
  data () {
    return {
      backenduiurl: process.env.VUE_APP_BACKEND_UI_URL,
      homeurl: process.env.VUE_APP_HOME_URL,
      showLoginModal: false,
      showSwitchModal: false,
      switchIdMUser: null,
      langs: ['en', 'de'],
      displayName: null,
      username: null,
      password: null,
      token: null,
      switchedUsername: null,
      loggedIn: false
    }
  },
  computed: {
    langNames () {
      return {
        en: this.$t('lang.en.label'),
        de: this.$t('lang.de.label')
      }
    },
    langFlags () {
      return {
        en: '🇺🇸',
        de: '🇩🇪'
      }
    },
    showUser () {
      return !!this.currentUser
    },
    showNodes () {
      return this.hasAnyRole([ADMIN_ROLE])
    },
    showAdmin () {
      return this.hasAnyRole([ADMIN_ROLE])
    },
    switchedAllowed () {
      return this.hasAnyRole([ADMIN_ROLE])
    },
    canSwitch () {
      return this.switchedAllowed && !this.switched
    },
    switchId () {
      if (this.switchIdMUser) {
        // return this.switchIdMUser.id
        return this.switchIdMUser
      }
      return null
    },
    unauthorizedConfirmed () {
      return this.$store.state.auth.unauthorized.confirmed
    },
    triggerLogin () {
      return this.$store.state.auth.triggerLogin
    }
  },
  watch: {
    unauthorizedConfirmed (confirmed) {
      if (confirmed && this.currentUser && this.currentUser.login === 'local' && this.currentUser.username) {
        this.username = this.currentUser.username
        this.showLoginModal = true
      }
    },
    triggerLogin (newValue) {
      if (newValue) {
        this.showLoginModal = true
        this.$store.commit('auth/triggerLogin', false)
      }
    }
  },
  methods: {
    langName (lang) {
      if (lang in this.langNames) {
        return this.langNames[lang]
      }
      return lang
    },
    langFlag (lang) {
      if (lang in this.langFlags) {
        return this.langFlags[lang]
      }
      return lang
    },
    changeLocale (lang) {
      this.$store.commit('prefs/lang', lang)
      asyncLanguageLoad(lang)
    },
    ping () {
      const requestStartedAt = new Date().getTime()
      this.$store.dispatch('auth/ping').then(
        () => this.makeToast(this.$i18n.t('ping.result.text', { time: new Date().getTime() - requestStartedAt }), this.$i18n.t('result.success.title'), 'success')
      ).catch(
        error => this.makeToast(this.$i18n.t('error.text', { status: 'na', message: error.message, id: 'ping' }), this.$i18n.t('result.error.title'), 'danger')
      )
    },
    gotoRecovery () {
      this.showLoginModal = false
      this.$router.push('/password/recovery')
    },
    resetLocalLogin () {
      this.password = ''
      this.token = ''
    },
    localLogin (event) {
      const vm = this
      event.preventDefault()
      const username = vm.username
      const password = vm.password
      const token = vm.token
      vm.resetLocalLogin()

      AuthService.localLogin(username, password, token).then(data => {
        if (data.success) {
          vm.makeToast(
            vm.$i18n.t('login.success.description'),
            vm.$i18n.t('result.success.title'),
            'info'
          )
          vm.showLoginModal = false

          if (vm.$router && vm.$route.name !== 'user' && !vm.$store.state.auth.redirectAfterLogin) {
            vm.$router.push({ name: 'user' }).catch((e) => true)
          }
        } else {
          vm.makeToast(
            vm.$i18n.t('login.error.description'),
            vm.$i18n.t('result.error.title'),
            'danger'
          )
        }
      }).catch(error => {
        console.error(error)
        vm.makeToast(
          vm.$i18n.t('error.text', { status: error.status, message: error.message }),
          vm.$i18n.t('result.error.title'),
          'danger')
      }).finally(
        () => vm.$store.dispatch('auth/ping')
      )
    },
    logout () {
      AuthService.logout().then(
        () => {
          this.makeToast(
            this.$i18n.t('logout.success.description'),
            this.$i18n.t('result.success.title'),
            'info'
          )
        }).catch(
        error => {
          this.makeToast(
            this.$i18n.t('error.text', { status: error.status, message: error.message }),
            this.$i18n.t('result.error.title'),
            'danger')
        })
    },
    switchUser () {
      AuthService.switch(this.switchId).then(
        response => {
          this.makeToast(this.$i18n.t('switched.text', {
            id: this.switchId,
            code: response.code
          }), this.$i18n.t('result.success.title'), 'success')
          if (this.$route.name !== 'user') {
            this.$router.push({ name: 'user' })
          }
        }).catch(
        error => this.makeToast(this.$i18n.t('error.text', {
          id: this.switchId,
          status: error.status,
          message: error.message
        }), this.$i18n.t('result.error.title'), 'danger')
      ).finally(
        () => this.resetSwitchUser()
      )
    },
    resetSwitchUser () {
      this.switchIdMUser = null
    },
    resume () {
      const savedSwitchedUserOriginalUsername = this.switchedUserOriginalUsername
      AuthService.resume().then(
        response => {
          this.makeToast(this.$i18n.t('resumed.text', {
            id: savedSwitchedUserOriginalUsername,
            code: response.code
          }), this.$i18n.t('result.success.title'), 'success')
        }
      ).catch(
        error => this.makeToast(this.$i18n.t('error.text', {
          id: this.switchId,
          status: error.status,
          message: error.message
        }), this.$i18n.t('result.error.title'), 'danger')
      )
    }
  }
}
</script>
